// src/user/pages/LaunchWorkflowPage.js
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useOutletContext } from 'react-router-dom';
import { UserProfileContext } from '../contexts/UserProfileContext';
import { AccessContext } from '../contexts/AccessProvider';
import { selfStartTask } from '../services/api/tasks';
import LoadingView from '../../shared/components/LoadingView';
import ErrorView from '../components/errors/ErrorView';
import { handleRetry } from '../../shared/utils/navigationUtils';

// Module-level set of access keys for which we have already started (or are
// in the middle of starting) a User Workflow task in this browser session.
// We deliberately keep this outside React state so that StrictMode's
// double-mount in development cannot start the workflow twice — the first
// mount registers the key here and the second mount (which sees the same
// module-level set) bails out.
const startedKeys = new Set();

/**
 * Public direct-launch page for User Workflows behind a short URL.
 *
 * Bootstraps access for the access_key, opportunistically attaches the
 * visitor's field_engineer_id (when present in the profile context — e.g.
 * a known service engineer hitting a public link), creates a fresh task
 * via self_start_task, and navigates the visitor straight into it.
 *
 * This page never opens the UserProfileModal.
 */
export default function LaunchWorkflowPage() {
  const { setTopBarProps } = useOutletContext();
  const { access_key, workflow_id } = useParams();
  const navigate = useNavigate();

  const { userProfile, markUserWorkflowAccess } = useContext(UserProfileContext);
  const { fetchAndValidateAccess } = useContext(AccessContext);

  const [error, setError] = useState(null);
  // Mirror the module-level guard so that even within a single component
  // instance we never re-enter the workflow-start path on re-renders.
  const startedRef = useRef(false);

  useEffect(() => {
    setTopBarProps({ headerText: 'Starting workflow…' });
  }, [setTopBarProps]);

  useEffect(() => {
    if (!access_key || !workflow_id) {
      setError('Missing access key or workflow.');
      return;
    }
    const dedupeKey = `${access_key}|${workflow_id}`;
    if (startedRef.current || startedKeys.has(dedupeKey)) return;
    startedRef.current = true;
    startedKeys.add(dedupeKey);

    // Mark this access key as a User Workflow session so UserLayout will not
    // pop the profile modal once we navigate downstream into the task.
    markUserWorkflowAccess(access_key);

    // Note: we deliberately do NOT use a `cancelled` flag tied to the
    // effect's cleanup. With React 18 StrictMode the dev-mode mount/unmount
    // cycle would otherwise abort the only in-flight request and the page
    // would sit on the loading view forever. The module-level dedupe set
    // already prevents duplicate calls; on a real unmount, leftover
    // setError/navigate calls are harmless.
    (async () => {
      try {
        // Validate the access key and warm the asset cache. Errors are
        // swallowed inside fetchAndValidateAccess.
        await fetchAndValidateAccess(access_key);

        const payload = { workflow_id };
        if (userProfile?.field_engineer_id) {
          payload.field_engineer_id = userProfile.field_engineer_id;
        }

        const task = await selfStartTask(access_key, payload);

        navigate(`/assets/access_key/${access_key}/task/${task.id}/start`, {
          replace: true,
        });
      } catch (err) {
        console.error('Failed to launch user workflow:', err);
        // Allow the user to retry by clearing the dedupe entry.
        startedKeys.delete(dedupeKey);
        startedRef.current = false;
        setError(err?.message || 'Failed to start the workflow.');
      }
    })();
  }, [
    access_key,
    workflow_id,
    fetchAndValidateAccess,
    markUserWorkflowAccess,
    navigate,
    userProfile?.field_engineer_id,
  ]);

  if (error) {
    return (
      <ErrorView
        title="Couldn't start the workflow"
        message={error}
        onRetry={handleRetry}
        onGoBack={() => navigate(`/assets/access_key/${access_key}`)}
      />
    );
  }

  return (
    <LoadingView
      title="Getting things ready"
      subtitle="We're starting the workflow for you."
      extra="Just a moment..."
      fullViewport
    />
  );
}
